/**
 * 이상한 방 - 콤보 규칙 (연속 정답 배수)
 *
 * 근거 문서: `이상한방-컨셉시트-v0.1.md` 4.3절(탄환 경제) · 4.5절(점수) · 5절(실패가 웃긴 톤)
 *            `이상한방-상태모델-v0.1.md` 12절(속도 보너스를 밀리초 경쟁으로 만들지 않는다)
 *
 * ## 이 파일이 존재하는 이유 - 오답에 값을 매기는 두 번째 축
 *
 * 이 게임의 유일한 보상은 "찾았다"의 순간이고, 5분에 그 순간이 열두 번뿐이다.
 * 열두 번이 전부 똑같은 값어치면 열두 번째 발견은 첫 번째의 반복일 뿐이다.
 * 콤보는 **뒤로 갈수록 발견이 비싸지게** 만들어 그 반복을 곡선으로 바꾼다.
 *
 * 동시에 오답의 값이 여기서 두 배가 된다. 지금까지 오답의 대가는 탄환 1발뿐이었고,
 * 시작 탄환이 16발(변화 12개)이라 **일단 다 쏴 보기**가 실제로 통하는 전략이었다.
 * 쌓아 둔 배수가 0으로 돌아가는 순간을 만들면, 탄환이 남아 있어도 방아쇠가 무거워진다.
 * 관찰 게임에서 사람을 멈춰 세우는 것은 자원이 아니라 **잃을 것**이다.
 *
 * ## 절대 규칙 - 시간으로는 끊기지 않는다
 *
 * 흔한 콤보는 타이머를 달고 있다(N초 안에 다음 정답). 이 게임에서는 그것이 곧 규칙의 배신이다.
 * 관찰 게임의 정답 구간은 "가만히 서서 방을 훑는 30초"이고, 그 30초가 콤보를 끊으면
 * 최적 전략이 **관찰을 멈추고 아무 데나 빨리 쏘는 것**으로 뒤집힌다.
 * 그 순간 이 게임은 틀린그림찾기가 아니라 반응속도 게임이 된다.
 *
 * 그래서 이 파일에는 **시간 인자가 하나도 없다.** 함수 시그니처에 `ms`가 없다는 사실 자체가
 * 규칙의 증명이다 - 나중에 누가 타이머를 붙이려면 이 파일의 API를 먼저 바꿔야 하고,
 * 그때 이 주석을 읽게 된다.
 *
 * ## 콤보 수와 배수는 다른 것이다
 *
 * **콤보 수(`combo`)에는 상한이 없고, 배수(`multiplier`)에만 상한이 있다.**
 * 상한을 콤보 수 쪽에 걸면 "12연속"이라는 기록이 사라지고, HUD가 11에서 멈춘 숫자를 띄우게 된다.
 * 사람이 자랑하는 것은 배수가 아니라 연속 횟수이므로, 세는 것과 값을 매기는 것을 갈라 둔다.
 */

import type { ShotOutcome } from './hunt'

/* --- 콤보를 움직이는 것 --------------------------------------------------------- */

/**
 * 콤보를 바꿀 수 있는 유일한 입력 - **한 발의 판정 결과**다.
 *
 * `hunt.ts`의 결과 종류에서 파생시키는 이유는 갈라짐 방지다. 판정에 네 번째 결과가 생기는 날,
 * 이 파일은 컴파일 에러로 멈춰 "그건 콤보를 어떻게 하느냐"를 묻는다.
 * 문자열 유니온을 손으로 베껴 두면 그 질문이 조용히 생략되고, 새 결과는 암묵적으로 콤보를 유지한다.
 */
export type ComboOutcome = ShotOutcome['kind']

/* --- 튜닝 ------------------------------------------------------------------- */

/**
 * 배수를 정하는 세 숫자. **퍼센트 정수로 들고 있는 것이 의도다.**
 *
 * 1.2를 실수로 다루면 `100 * 1.2`가 120.00000000000001이 되고, 그 오차가 그대로 점수에 실려
 * 결과 화면에 "2740.0000000000005"가 뜬다. 배수 자체는 사람에게 보여 줄 때만 실수로 바꾼다
 * (`comboMultiplier`). **점수 계산은 언제나 정수 퍼센트로 한다**(`comboMultiplierPercent`).
 */
type ComboRulesValues = {
  /** 콤보 없음·첫 발견의 배수(퍼센트). 100 = 1.0배. */
  basePercent: number
  /** 정답 하나가 얹는 양(퍼센트). 20 = +0.2배. */
  stepPercent: number
  /** 배수 상한(퍼센트). 300 = 3.0배. */
  maxPercent: number
}

export type ComboRules = Readonly<ComboRulesValues>

/** 퍼센트를 배수로 옮기는 나눗셈의 분모. 매직넘버 100을 코드에 흘리지 않기 위해서만 존재한다. */
const PERCENT_SCALE = 100

/** 콤보가 없는 상태. 라운드 시작과 오답 직후의 값이다. */
export const NO_COMBO = 0

/**
 * 기본 콤보 규칙: 1.0 → 1.2 → 1.4 ... 3.0에서 멈춤.
 *
 * 상한이 **11연속에서 걸리도록** 잡았다(변화 12개 기준). 라운드가 끝나기 직전에야 천장에 닿으므로
 * 대부분의 판에서 배수는 계속 오르는 중이고, 그래서 마지막 한 개까지 긴장이 유지된다.
 * 반대로 상한을 5.0쯤으로 올리면 완주 점수가 미완주의 세 배를 넘어가, 한 번 끊긴 판을
 * **그 자리에서 버리는 것**이 합리적인 선택이 된다. 그건 "한 판 더"(8절)의 반대말이다.
 *
 * 전부 플레이테스트 대상이다(컨셉시트 7절). 바꿔 볼 때는 `makeComboRules`로 만든다.
 */
export const COMBO_RULES: ComboRules = {
  basePercent: 100,
  stepPercent: 20,
  maxPercent: 300,
}

/**
 * 규칙을 부분 덮어쓰기로 만든다. **검사가 이 함수의 존재 이유다.**
 *
 * 잘못된 규칙은 조용히 이상한 점수를 만든다 - `maxPercent`가 `basePercent`보다 작으면
 * 배수가 1.0 아래로 내려가 정답이 감점이 되고, 그건 컨셉시트 4.5절("미발견도 감점하지 않는다")의
 * 정반대다. 그런 값은 결과 화면을 보고 나서야 발견되므로, 만드는 자리에서 터뜨린다.
 */
export function makeComboRules(overrides: Partial<ComboRulesValues> = {}): ComboRules {
  const rules: ComboRules = { ...COMBO_RULES, ...overrides }

  if (!Number.isFinite(rules.basePercent) || rules.basePercent <= 0) {
    throw new RangeError(
      `makeComboRules: basePercent는 양수여야 한다 (받은 값: ${String(rules.basePercent)}).`,
    )
  }
  if (!Number.isFinite(rules.stepPercent) || rules.stepPercent < 0) {
    throw new RangeError(
      `makeComboRules: stepPercent는 0 이상이어야 한다 (받은 값: ${String(rules.stepPercent)}). ` +
        '음수면 연속 정답이 감점이 된다.',
    )
  }
  if (!Number.isFinite(rules.maxPercent) || rules.maxPercent < rules.basePercent) {
    throw new RangeError(
      `makeComboRules: maxPercent는 basePercent(${String(rules.basePercent)}) 이상이어야 한다 ` +
        `(받은 값: ${String(rules.maxPercent)}). 상한이 더 낮으면 배수가 1.0 아래로 내려간다.`,
    )
  }

  return rules
}

/* --- 콤보 수 ------------------------------------------------------------------ */

/**
 * 콤보 수를 성한 값으로 접는다.
 *
 * 던지지 않는 이유는 호출부의 성격이다. 콤보는 HUD·사운드·점수가 전부 읽는 값이고,
 * 어딘가에서 `undefined`가 섞여 들어왔을 때 **소리가 던져서 사격이 멈추면** 그 판은 끝난다.
 * 이상한 값은 "콤보 없음"으로 접는 것이 언제나 안전한 방향이다 - 점수를 부풀리지 않는다.
 */
function normalizeCombo(combo: number): number {
  if (!Number.isFinite(combo) || combo <= NO_COMBO) return NO_COMBO
  return Math.floor(combo)
}

/**
 * 한 발을 쏜 뒤의 콤보 수. **이 게임의 콤보 규칙 전체가 이 세 줄이다.**
 *
 * | 결과 | 콤보 | 왜 |
 * |---|---|---|
 * | `hit` | +1 | 연속의 정의 |
 * | `miss` | 0 | **끊는 것은 오답 하나뿐이다** |
 * | `already-found` | 유지 | 탄환도 안 먹는 행위다(4.3절). 벌하지 않는 것이 규칙이므로 콤보도 건드리지 않는다 |
 *
 * 세 번째 줄이 특히 중요하다. 이미 찾은 곳을 다시 쏘는 것은 실수가 아니라 **기억의 한계**이고
 * (12개를 5분간 추적하면 반드시 일어난다), 여기서 콤보를 끊으면 사람은 "찾은 곳을 다시 쏠까 봐"
 * 화면을 덜 훑게 된다. 관찰을 줄이는 규칙은 관찰 게임에서 잘못된 규칙이다.
 *
 * 시간이 인자에 없다는 것도 규칙이다 - 이 파일 머리말의 "시간으로는 끊기지 않는다"가 그것이다.
 */
export function comboAfter(combo: number, outcome: ComboOutcome): number {
  const streak = normalizeCombo(combo)

  switch (outcome) {
    case 'hit':
      return streak + 1
    case 'miss':
      return NO_COMBO
    case 'already-found':
      return streak
  }
}

/** 이 결과가 쌓여 있던 콤보를 끊는가. 연출(`comboBroken`)을 낼지 판단하는 자리에서 쓴다. */
export function breaksCombo(combo: number, outcome: ComboOutcome): boolean {
  return outcome === 'miss' && normalizeCombo(combo) > NO_COMBO
}

/* --- 배수 -------------------------------------------------------------------- */

/**
 * 콤보 수 → 배수(퍼센트 정수).
 *
 * **첫 발견은 1.0배다**(combo === 1). 첫 정답부터 배수가 붙으면 "콤보"라는 말이 무의미해지고,
 * 무엇보다 오답 한 번의 손실을 사람이 계산할 수 없게 된다 - 끊긴 뒤 돌아오는 자리가
 * 시작 자리와 같아야 "얼마를 잃었는가"가 눈에 보인다.
 *
 * 점수 계산은 전부 이 정수 퍼센트로 한다. 실수 배수를 곱하면 부동소수 오차가 총점에 남는다.
 */
export function comboMultiplierPercent(combo: number, rules: ComboRules = COMBO_RULES): number {
  const streak = normalizeCombo(combo)
  if (streak <= 1) return rules.basePercent

  const raised = rules.basePercent + (streak - 1) * rules.stepPercent
  return Math.min(raised, rules.maxPercent)
}

/**
 * 콤보 수 → 배수(1.0 · 1.2 ...). **표시 전용이다.**
 * 점수에 곱하지 마라 - 그 길은 `comboMultiplierPercent`다(부동소수 오차가 총점에 남는다).
 */
export function comboMultiplier(combo: number, rules: ComboRules = COMBO_RULES): number {
  return comboMultiplierPercent(combo, rules) / PERCENT_SCALE
}

/**
 * 배수가 상한에 닿았는가.
 *
 * HUD가 필요로 한다 - 천장에 닿은 뒤에도 숫자가 계속 오르면(콤보 수에는 상한이 없다)
 * 사람은 배수도 계속 오르는 줄 알고, 결과 화면에서 계산이 맞지 않는다고 느낀다.
 * 닿은 순간부터는 배수 대신 "MAX"를 띄우는 것이 정직하다.
 */
export function isComboAtCap(combo: number, rules: ComboRules = COMBO_RULES): boolean {
  return comboMultiplierPercent(combo, rules) >= rules.maxPercent
}

/**
 * 사운드에 넘길 콤보 단계(반음 수). 첫 발견이 0단계다.
 *
 * `audio/sounds.ts`가 이 값을 반음으로 읽어 발견음을 올린다. **여기서 상한을 걸지 않는다** -
 * 상한의 이유가 다르기 때문이다. 배수의 상한은 밸런스이고 음정의 상한은 귀(고역의 통증)라서,
 * 한쪽 숫자를 바꿀 때 다른 쪽이 딸려 오면 안 된다. 음정 상한은 사운드 쪽이 자기 값으로 건다.
 */
export function comboPitchStep(combo: number): number {
  return Math.max(0, normalizeCombo(combo) - 1)
}

/* --- 퍼펙트 ------------------------------------------------------------------- */

/**
 * 무오답 전량 발견인가.
 *
 * **콤보 수 하나로 판정한다.** 오답이 유일한 절단 사유이므로, 콤보가 변화 개수만큼 쌓였다는 것은
 * 그 라운드에 오답이 한 발도 없었다는 것과 같은 말이다. 별도의 오답 카운터를 두면
 * "콤보는 끊겼는데 퍼펙트" 같은 모순 상태가 존재할 수 있게 되고, 두 값이 갈라지는 날이 온다.
 *
 * 변화가 0개인 라운드를 퍼펙트로 치지 않는 이유: 그건 성취가 아니라 생성기의 버그다
 * (`gameState.enterHunt`가 실제로 터뜨린다).
 */
export function isPerfectRun(combo: number, changeCount: number): boolean {
  if (!Number.isFinite(changeCount) || changeCount <= 0) return false
  return normalizeCombo(combo) >= changeCount
}
